import styles from "./Dashboard.module.scss";

import { Button, Checkbox, DatePicker, Dropdown, Input, Layout, Popover, Space, Table, Tag, Typography } from "antd";
import { ApiService } from "api/api.service";
import PageTitle from "components/PageTitle";
import DashboardLayout from "layout/DashboardLayout";
import { useEffect, useState } from "react";
import {
  TbArrowsUpDown,
  TbCalendarStats,
  TbChevronLeft,
  TbChevronRight,
  TbColumns3,
  TbLoader2,
  TbSearch,
  TbSortAscendingNumbers,
  TbSortDescending,
  TbSortDescendingNumbers,
  TbX,
} from "react-icons/tb";
import { LEAD_STATUS_COLOURS, PROJECT_INFO } from "utils/constants";
import { columns, PopulatedLeadDto } from "./Dashboard.columns";
import LeadSiderContent from "./components/LeadSiderContent";
import { LeadSchemaDto, LeadStatus, MakingPayments, PaymentFrequency } from "dtos/lead.dto";
import { LuListFilter } from "react-icons/lu";
import dayjs from "dayjs";
import { ItemType, MenuItemGroupType, MenuItemType } from "antd/es/menu/interface";
import { useAppSelector } from "store";
import { getDefaultPage } from "layout/DashboardLayout/DashboardLayout.utils";
import { useNavigate } from "react-router-dom";

type T_Params = Parameters<typeof ApiService.lead.useGetAllPaginated>[0];

function Dashboard() {
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.user);
  const [params, setParams] = useState<T_Params>({
    page: 1,
    limit: 20,
  });
  const quoteList = ApiService.lead.useGetAllPaginated(params);

  const [selectedLead, setSelectedLead] = useState<PopulatedLeadDto>();
  const [showColumns, setShowColumns] = useState<
    Partial<Record<keyof (LeadSchemaDto & LeadSchemaDto["questionaire"]), boolean>>
  >({
    name: true,
    email: true,
    phone: true,
    createdAt: true,
    status: true,
    debt_amount: true,
    is_making_payments: true,
    payment_amount: true,
    payment_frequency: false,
    has_pending_lawsuit: false,
  });
  const sortOrder = params?.sort && Object.values(params?.sort)?.length > 0 ? Object.values(params?.sort)[0] : -1;

  useEffect(() => {
    const page = user.role ? getDefaultPage(user.role) : null;
    if (page) navigate(page);
  }, [user.role]);

  return (
    <DashboardLayout
      headerLeft={
        <Typography.Title level={5} className="!m-0">
          Website Leads
        </Typography.Title>
      }
    >
      <Layout>
        <PageTitle title="Website Leads" pageTitle={[{ title: "Dashboard" }]} />

        <Layout.Content
          style={{ paddingRight: selectedLead ? 350 : 0, background: "white", transition: "padding 250ms ease-in-out" }}
        >
          <div className="flex justify-between gap-3 p-3">
            <div className="inline-flex gap-3 items-center">
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: columns.map((el) => {
                    const key = String(el.key) as keyof typeof showColumns;
                    return {
                      key: key,
                      label: (
                        <div className="flex gap-2" key={el.key}>
                          <Checkbox checked={showColumns[key]} />
                          <span>{el.title?.toString()}</span>
                        </div>
                      ),
                    };
                  }),
                  onClick: ({ key }) => {
                    setShowColumns((prev) => {
                      return { ...prev, [key]: !(prev as any)[key] };
                    });
                  },
                }}
              >
                <Button icon={<TbColumns3 />}>
                  Columns{" "}
                  <Tag bordered={false} className="!m-0 !bg-gray-100 !font-medium">
                    {
                      columns.filter((el) => {
                        const key = String(el.key) as keyof typeof showColumns;
                        return showColumns[key];
                      }).length
                    }
                  </Tag>
                </Button>
              </Dropdown>
              <Dropdown
                trigger={["click"]}
                menu={{
                  // selectable: true,
                  items: [
                    {
                      type: "group",
                      // key: "status",
                      label: "Status",
                      children: Object.entries(LeadStatus).map(([key, value]) => ({
                        label: (
                          <div className="flex gap-2" key={key}>
                            <Checkbox checked={params?.filter?.status?.includes(value)} />
                            <Tag color={LEAD_STATUS_COLOURS[value]}>{key.replace(/_/g, " ")}</Tag>
                          </div>
                        ),
                        key: `status:${value}`,
                      })),
                    },
                    {
                      type: "divider",
                    },
                    {
                      type: "group",
                      // key: "is_making_payments",
                      label: "Currently Paying",
                      children: Object.values(MakingPayments).map((el) => {
                        return {
                          key: `questionaire.is_making_payments:${el}`,
                          label: (
                            <div className="flex gap-2" key={el}>
                              <Checkbox checked={params?.filter?.["questionaire.is_making_payments"]?.includes(el)} />
                              <span>{el}</span>
                            </div>
                          ),
                        };
                      }),
                    },
                    {
                      type: "divider",
                    },
                    {
                      type: "group",
                      // key: "payment_frequency",
                      label: "Payment Frequency",
                      children: Object.values(PaymentFrequency).map((el) => {
                        return {
                          key: `questionaire.payment_frequency:${el}`,
                          label: (
                            <div className="flex gap-2" key={el}>
                              <Checkbox checked={params?.filter?.["questionaire.payment_frequency"]?.includes(el)} />
                              <span>{el}</span>
                            </div>
                          ),
                        };
                      }),
                    },
                    {
                      type: "divider",
                    },
                    {
                      type: "group",
                      label: "Has Pending Lawsuit?",
                      children: ["Yes", "No"].map((el) => {
                        return {
                          key: `questionaire.has_pending_lawsuit:${el}`,
                          label: (
                            <div className="flex gap-2" key={el}>
                              <Checkbox checked={params?.filter?.["questionaire.has_pending_lawsuit"]?.includes(el)} />
                              <span>{el}</span>
                            </div>
                          ),
                        };
                      }),
                    },
                  ].map((el) => {
                    if (el.type === "group") {
                      return {
                        ...el,
                        children: el.children?.map((child) => {
                          const [k, value] = child.key.split(":");
                          const key = k as keyof NonNullable<typeof params.filter>;

                          return {
                            ...child,
                            onClick: () => {
                              setParams((prev) => {
                                const prevArr = prev?.filter?.[key] ?? [];
                                let newArr: string[] = [];
                                if (prevArr.includes(value)) {
                                  newArr = prevArr.filter((i) => i !== value);
                                } else {
                                  newArr = [...prevArr, value];
                                }
                                return { ...prev, filter: { ...(prev?.filter ?? {}), [key]: newArr } };
                              });
                            },
                          };
                        }),
                      } as MenuItemGroupType<MenuItemType>;
                    } else {
                      return el as MenuItemType;
                    }
                  }),
                }}
              >
                <Button icon={<LuListFilter />}>
                  Filter{" "}
                  {params.filter ? (
                    <Tag bordered={false} className="!m-0 !bg-gray-100 !font-medium">
                      {
                        Object.values(params.filter)
                          .map((el) => el)
                          .flat()?.length
                      }
                    </Tag>
                  ) : null}
                </Button>
              </Dropdown>
              <Space.Compact>
                <Dropdown
                  trigger={["click"]}
                  menu={{
                    selectable: true,
                    selectedKeys: params?.sort ? Object.keys(params.sort) : [],
                    onSelect: ({ key }) => setParams((prev) => ({ ...prev, sort: { [key]: sortOrder } as any })),
                    items: [
                      {
                        key: "name",
                        label: "Name",
                      },
                      {
                        key: "createdAt",
                        label: "Created at",
                      },
                      {
                        key: "debt_amount",
                        label: "Debt Amount",
                      },
                      {
                        key: "payment_amount",
                        label: "Payment Amount",
                      },
                    ],
                  }}
                >
                  <Button
                    className="border-gray-300"
                    variant={params?.sort ? "filled" : "outlined"}
                    color={params?.sort ? "primary" : "default"}
                    icon={<TbArrowsUpDown />}
                  >
                    Sort
                  </Button>
                </Dropdown>
                {params?.sort && Object.keys(params?.sort)?.length > 0 ? (
                  <>
                    <Button
                      color="default"
                      className="border-gray-300 px-2"
                      icon={sortOrder === -1 ? <TbSortDescendingNumbers /> : <TbSortAscendingNumbers />}
                      onClick={() =>
                        setParams((prev) => {
                          if (!prev?.sort) return prev;
                          const key = Object.keys(prev?.sort)?.[0];
                          return { ...prev, sort: { [key]: sortOrder === 1 ? -1 : 1 } };
                        })
                      }
                    >
                      <Typography.Text className="text-xs w-10">{sortOrder === -1 ? "DESC" : "ASC"}</Typography.Text>
                    </Button>
                    <Button icon={<TbX />} onClick={() => setParams((prev) => ({ ...prev, sort: undefined }))} />
                  </>
                ) : null}
              </Space.Compact>
              <Space.Compact>
                <Popover
                  trigger={"click"}
                  arrow={false}
                  placement="bottomLeft"
                  content={() => {
                    return (
                      <div className="flex flex-col gap-2">
                        <Typography.Text>Start Date</Typography.Text>
                        <DatePicker
                          value={params?.startDate ? dayjs(params?.startDate) : undefined}
                          onChange={(date) => {
                            setParams((prev) => ({ ...prev, startDate: date ? date.toISOString() : undefined }));
                          }}
                          disabledDate={(date) => {
                            if (params?.endDate) {
                              return dayjs(params?.endDate).isBefore(date);
                            } else return false;
                          }}
                        />
                        <Typography.Text>End Date</Typography.Text>
                        <DatePicker
                          value={params?.endDate ? dayjs(params?.endDate) : undefined}
                          onChange={(date) => {
                            setParams((prev) => ({ ...prev, endDate: date ? date.toISOString() : undefined }));
                          }}
                          disabledDate={(date) => {
                            if (params?.startDate) {
                              return dayjs(params?.startDate).isAfter(date);
                            } else return false;
                          }}
                        />
                      </div>
                    );
                  }}
                >
                  <Button
                    className="border-gray-300"
                    variant={params?.startDate || params?.endDate ? "filled" : "outlined"}
                    color={params?.startDate || params?.endDate ? "primary" : "default"}
                    icon={<TbCalendarStats />}
                  >
                    Created At
                  </Button>
                </Popover>
                {params?.startDate || params?.endDate ? (
                  <>
                    <Button
                      icon={<TbX />}
                      onClick={() => setParams((prev) => ({ ...prev, startDate: undefined, endDate: undefined }))}
                    />
                  </>
                ) : null}
              </Space.Compact>
            </div>
            <div className="inline-flex gap-3 items-center">
              <Input
                prefix={<TbSearch />}
                placeholder="Search leads..."
                onPressEnter={(e) => {
                  setParams((prev) => ({ ...prev, search: e.currentTarget.value }));
                }}
                allowClear
                onClear={() => {
                  setParams((prev) => ({ ...prev, search: undefined }));
                }}
              />
            </div>
          </div>
          <Table<PopulatedLeadDto>
            scroll={{ x: "max-content", y: "calc(100vh - 202px)" }}
            virtual={false}
            size="small"
            loading={{
              spinning: quoteList.isFetching,
              indicator: <TbLoader2 size={48} className="text-brand-500 animate-spin" />,
            }}
            columns={columns.filter((el) => {
              const key = String(el.key) as keyof typeof showColumns;
              return key in showColumns && showColumns[key] == true;
            })}
            dataSource={quoteList.data?.data}
            className={`${styles.table}`}
            rowClassName={(record) =>
              `cursor-pointer ${record._id === selectedLead?._id ? "bg-violet-100 hover:!bg-violet-200" : ""}`
            }
            onRow={(record) => {
              return {
                onClick: () => {
                  setSelectedLead(record);
                },
              };
            }}
            rowKey={(record) => record._id} // Add this line
            pagination={{
              hideOnSinglePage: true,
              showQuickJumper: true,
              showPrevNextJumpers: true,
              nextIcon: (
                <Button
                  className="ant-pagination-item-link !flex items-center justify-center"
                  type="text"
                  icon={<TbChevronRight size={20} />}
                />
              ),
              prevIcon: (
                <Button
                  className="ant-pagination-item-link !flex items-center justify-center"
                  type="text"
                  icon={<TbChevronLeft size={20} />}
                />
              ),
              showTitle: true,
              showTotal: (total, range) => {
                return `Showing ${range[0]}-${range[1]} of ${total} leads`;
              },
              showSizeChanger: true,
              total: quoteList.data?.totalObjects,
              current: params.page,
              pageSize: params.limit,
              onChange: (page, pageSize) => {
                setParams({ page: page, limit: pageSize });
              },
            }}
          />
        </Layout.Content>
        {/* <Layout.Sider
          theme="light"
          collapsed={!selectedLead}
          collapsedWidth={0}
          width={350}
          style={{
            position: "fixed",
            top: PROJECT_INFO.layout.headerHeight,
            height: `calc(100vh - ${PROJECT_INFO.layout.headerHeight}px)`,
          }}
          className="border-neutral-200 border-0 border-l border-solid right-0 bottom-0 z-10"
        > */}
        <div
          style={{
            width: 350,
            position: "fixed",
            top: PROJECT_INFO.layout.headerHeight,
            height: `calc(100vh - ${PROJECT_INFO.layout.headerHeight}px)`,
            transform: `translateX(${selectedLead ? 0 : 350}px)`,
            transition: "transform 250ms ease-in-out",
          }}
          className="border-neutral-200 border-0 border-l border-solid right-0 bottom-0 z-10 bg-white"
        >
          {selectedLead ? (
            <LeadSiderContent
              selectedLead={selectedLead}
              onSiderClose={() => {
                setSelectedLead(undefined);
              }}
            />
          ) : null}
        </div>
        {/* </Layout.Sider> */}
      </Layout>
    </DashboardLayout>
  );
}

export default Dashboard;
